import { useCompanyVisibilityState } from '@aibees/elephant/hooks';
import { Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Badge, Box, Tooltip, Typography } from '@mui/material';
import { memo, type PropsWithChildren } from 'react';
import type { PreviousEmploymentBadgeProps as Props } from './types';

export const PreviousEmploymentBadge = memo(
  ({
    className,
    children,
    badgeSize,
    tooltipPlacement,
    personData,
  }: PropsWithChildren<Props>) => {
    const {
      isNoLongerEmployed,
      noPreviousCompanyDataAvailable,
      hasOnlyPreviousCompanyName,
      wasPreviouslySelfEmployedOrFreelancer,
    } = useCompanyVisibilityState(personData);

    const shouldShow =
      isNoLongerEmployed ||
      noPreviousCompanyDataAvailable ||
      hasOnlyPreviousCompanyName ||
      wasPreviouslySelfEmployedOrFreelancer;

    if (!shouldShow) {
      return (
        <Box
          className={classnames('PreviousEmploymentBadge', className)}
          sx={{ display: 'inline-flex' }}
        >
          {children}
        </Box>
      );
    }

    const tooltipTitle = (
      <Typography variant="body5" fontWeight={500} textAlign="center">
        Based on previous employment
      </Typography>
    );

    if (children) {
      return (
        <Box
          className={classnames('PreviousEmploymentBadge', className)}
          sx={{ display: 'inline-flex' }}
        >
          <Badge
            color="warning"
            variant="soft"
            size={badgeSize ?? 16}
            badgeContent={
              <Tooltip
                placement={tooltipPlacement ?? 'bottom'}
                title={tooltipTitle}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Icon name="undo" color="warning" />
                </Box>
              </Tooltip>
            }
          >
            {children}
          </Badge>
        </Box>
      );
    }

    return (
      <Tooltip
        className={classnames('PreviousEmploymentBadge', className)}
        placement={tooltipPlacement ?? 'bottom'}
        title={tooltipTitle}
      >
        <Badge
          color="warning"
          variant="soft"
          size={badgeSize ?? 16}
          badgeContent={<Icon name="undo" color="warning" />}
        />
      </Tooltip>
    );
  }
);

PreviousEmploymentBadge.displayName = 'PreviousEmploymentBadge';
